import { useAppSelector } from "hooks/store";
import React, { useEffect, useMemo, useRef, useState } from "react"; // === ADDED BY SAMPATH – 2026-01-06 ===
import { useSearchParams } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"; // === ADDED BY SAMPATH – 2026-01-06 ===
import ChevronRightIcon from "@mui/icons-material/ChevronRight"; // === ADDED BY SAMPATH – 2026-01-06 ===
import MenuIcon from "@mui/icons-material/Menu"; // === ADDED BY SAMPATH – 2026-01-06 ===
import CloseIcon from "@mui/icons-material/Close"; // === ADDED BY SAMPATH – 2026-01-06 ===

const HorizontalMenu = () => {
  const sideBarMenu = useAppSelector(({ main }) => main.sideBarMenu);
  const displayOption = useAppSelector(
    ({ main }) => main.settings.DeptDisplayOption
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const product = useMemo(
    () => +(searchParams.get("product") || 0),
    [searchParams]
  );

  // === ADDED BY SAMPATH – 2026-01-06 ===
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  // inherit theme/app font everywhere
  const fontInherit = "inherit";

  // theme color helpers (uses your existing Tailwind theme classes)
  // - Active uses primary with transparency
  // - Hover uses primary with lighter transparency
  const activeBgClass = "bg-primary/10";
  const hoverBgClass = "hover:bg-primary/10";
  const activeTextClass = "text-primary";
  const normalTextClass = "text-deptColor";

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    const left = el.scrollLeft;
    setCanLeft(left > 2);
    setCanRight(left < max - 2);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => updateArrows();
    el.addEventListener("scroll", onScroll, { passive: true });

    const ro = new ResizeObserver(() => updateArrows());
    ro.observe(el);

    const t = window.setTimeout(updateArrows, 50);

    return () => {
      el.removeEventListener("scroll", onScroll as any);
      ro.disconnect();
      window.clearTimeout(t);
    };
  }, [sideBarMenu.length]);

  const scroll = (dir: "left" | "right") => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollBy({
      left: dir === "left" ? -240 : 240,
      behavior: "smooth",
    });
  };

  // Close menu on ESC + lock page scroll while menu open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);

    const prev = document.body.style.overflow;
    if (menuOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = prev || "";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev || "";
    };
  }, [menuOpen]);

  // animate panel open smoothly (slide from left)
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const r = requestAnimationFrame(() => {
      el.classList.remove("-translate-x-full");
      el.classList.add("translate-x-0");
    });
    return () => cancelAnimationFrame(r);
  }, [menuOpen]);

  const closeWithSlide = () => {
    const el = panelRef.current;
    if (!el) return setMenuOpen(false);
    el.classList.remove("translate-x-0");
    el.classList.add("-translate-x-full");
    window.setTimeout(() => setMenuOpen(false), 220);
  };

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // NEW: fire event so Listing.tsx can auto-scroll to category section
  const fireCategoryScroll = (id: number) => {
    window.dispatchEvent(
      new CustomEvent("dcs:categoryScroll", { detail: { id } })
    );
  };
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-12 ===
  // NEW: when active category changes (auto-select from scroll), keep selected chip visible
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const active = el.querySelector(
      `[data-cat-btn="${product}"]`
    ) as HTMLElement | null;
    if (!active) return;

    active.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [product]);
  // === END SAMPATH ===
  // === END SAMPATH ===

  return (
    <div className="relative bg-white border-b border-primary">
      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      <button
        onClick={() => setMenuOpen(true)}
        className="absolute left-2 top-1/2 -translate-y-1/2 z-30 bg-white/95 backdrop-blur-sm shadow-md rounded-full w-10 h-10 flex items-center justify-center hover:bg-white transition"
        aria-label="Open full menu"
      >
        <MenuIcon className="text-gray-700" />
      </button>
      {/* === END SAMPATH === */}

      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      {canLeft && (
        <div className="pointer-events-none absolute left-0 top-0 h-full w-10 bg-gradient-to-r from-white to-transparent z-10" />
      )}
      {canRight && (
        <div className="pointer-events-none absolute right-0 top-0 h-full w-10 bg-gradient-to-l from-white to-transparent z-10" />
      )}

      {canLeft && (
        <button
          onClick={() => scroll("left")}
          className="absolute left-14 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm shadow-md rounded-full w-9 h-9 flex items-center justify-center hover:bg-white transition"
          aria-label="Scroll left"
        >
          <ChevronLeftIcon className="text-gray-700" />
        </button>
      )}

      {canRight && (
        <button
          onClick={() => scroll("right")}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/90 backdrop-blur-sm shadow-md rounded-full w-9 h-9 flex items-center justify-center hover:bg-white transition"
          aria-label="Scroll right"
        >
          <ChevronRightIcon className="text-gray-700" />
        </button>
      )}
      {/* === END SAMPATH === */}

      <div
        ref={scrollRef} // === ADDED BY SAMPATH – 2026-01-06 ===
        // === ADDED BY SAMPATH – 2026-01-06 ===
        style={{
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          paddingLeft: "56px",
        }}
        // === END SAMPATH ===
        className="flex space-x-4 px-3 py-3 bg-white overflow-x-auto"
      >
        {/* === ADDED BY SAMPATH – 2026-01-06 === */}
        <style>
          {`
            .hide-h-scrollbar::-webkit-scrollbar { display: none; height: 0; }
          `}
        </style>
        {/* === END SAMPATH === */}

        <div className="flex space-x-4 hide-h-scrollbar">
          {sideBarMenu.map(({ id, categoryDescription, imageUrl }) => (
            <div
              key={id}
              // === ADDED BY SAMPATH – 2026-01-12 ===
              data-cat-btn={id}
              // === END SAMPATH ===
              className={`capitalize transition-all duration-300 py-2 px-4 rounded-full cursor-pointer font-semibold flex gap-3 items-center border border-black/5 ${
                product === id
                  ? "bg-primary text-themeSelColor shadow-sm"
                  : "bg-white hover:bg-primary/10 text-deptColor"
              } flex-shrink-0 `}
              onClick={() => {
                // === ADDED BY SAMPATH – 2026-01-12 ===
                // NEW: auto scroll Listing to selected category section
                fireCategoryScroll(Number(id));
                // === END SAMPATH ===

                let search = searchParams;
                search.set("product", id.toString());
                setSearchParams(search);
              }}
            >
              {["Image", "Both"].includes(displayOption) && !!imageUrl && (
                <img
                  src={imageUrl}
                  alt="logo"
                  className="w-8 h-8 rounded-full object-cover"
                />
              )}

              {["Text", "Both"].includes(displayOption) && (
                <span className="block overflow-hidden overflow-ellipsis whitespace-normal line-clamp-2">
                  {categoryDescription.toLowerCase()}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* === ADDED BY SAMPATH – 2026-01-06 === */}
      {menuOpen && (
        <div className="fixed inset-0 z-[9999]">
          {/* Backdrop with fade */}
          <div
            className="absolute inset-0 bg-black/40 opacity-100 transition-opacity duration-200"
            onClick={closeWithSlide}
          />

          {/* Panel: remove extra empty space by using fixed width (not 84%) + keep rounded right corners */}
          <div
            ref={panelRef}
            className="absolute left-0 top-0 h-full w-[320px] max-w-[88vw] bg-white shadow-2xl rounded-tr-2xl rounded-br-2xl overflow-hidden transform -translate-x-full transition-transform duration-200 ease-out"
            style={{ fontFamily: fontInherit }}
          >
            {/* Header (compact) */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-black/10">
              <div
                className="font-semibold text-[15px] text-gray-900"
                style={{ fontFamily: fontInherit }}
              >
                Full Menu
              </div>

              <button
                onClick={closeWithSlide}
                className="bg-gray-100 hover:bg-gray-200 transition rounded-full w-9 h-9 flex items-center justify-center"
                aria-label="Close menu"
              >
                <CloseIcon className="text-gray-700" />
              </button>
            </div>

            {/* List: no ALL CAPS, no extra spacing, theme colors + transparency */}
            <div
              className="px-2 py-2 overflow-y-auto"
              style={{
                maxHeight: "calc(100vh - 56px)",
                fontFamily: fontInherit,
              }}
            >
              {sideBarMenu.map(({ id, categoryDescription }) => {
                const active = product === id;

                return (
                  <button
                    key={id}
                    onClick={() => {
                      // === ADDED BY SAMPATH – 2026-01-12 ===
                      // NEW: auto scroll Listing to selected category section
                      fireCategoryScroll(Number(id));
                      // === END SAMPATH ===

                      let search = searchParams;
                      search.set("product", id.toString());
                      setSearchParams(search);
                      closeWithSlide();
                    }}
                    className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition ${
                      active
                        ? `${activeBgClass} ${activeTextClass} font-semibold`
                        : `${hoverBgClass} ${normalTextClass} font-medium`
                    }`}
                    style={{ fontFamily: fontInherit }}
                  >
                    <span
                      className="text-[13px] leading-5"
                      style={{ fontFamily: fontInherit }}
                    >
                      {categoryDescription}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      {/* === END SAMPATH === */}
    </div>
  );
};

export default HorizontalMenu;